import { word } from "@/modules/core/utils/i18n"
import type { Language } from "@/modules/language/utils/languageUtils"
import { getScoreDifficultyWording, ScoreResultType, ScoreType } from "@/modules/scores/scoreTypes"
import { getDisplayedScore, getScoreResultTypeCaption, getScoreTypeLabel } from "@/modules/scores/scoreUtils"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { HUUnit } from "@/modules/utils/unitType"

type ExerciseDetailModel = {
  scoreTypeCaption: string
  displayedScore: string
  displayedScoreCompletionDifficulty: string | null
  isScoreCompletionPersonalRecord: boolean
  displayedScoreSetCompletionList: string[] | null
  displayCompleteExerciseScoreOrFeedbackButton: boolean
  shouldDisplayScoreSection: boolean
}

export function createExerciseDetailModel(
  exercise: SessionBlockExerciseEntity,
  unit: HUUnit,
  language: Language,
): ExerciseDetailModel {
  function getScoreType(): string | null {
    if (exercise.scoreSetConfigurations.length === 0) return null
    if (exercise.scoreSetConfigurations.length === 1 && !!exercise.scoreSetConfigurations[0])
      return getScoreTypeLabel(exercise.scoreSetConfigurations[0].scoreType)
    if (
      exercise.scoreSetConfigurations.length > 1 &&
      exercise.scoringOption &&
      exercise.scoringOption === ScoreResultType.SET_N_FOR_SCOREBOARD
    ) {
      return getScoreTypeLabel(
        exercise.scoreSetConfigurations.find((scoreSet) => scoreSet.useForScoring)?.scoreType ?? ScoreType.NO_SCORE,
      )
    }
    if (
      exercise.scoreSetConfigurations.length > 1 &&
      !!exercise.scoreSetConfigurations[0] &&
      !!exercise.scoringOption &&
      exercise.scoreSetConfigurations.every(
        (scoreSet) => scoreSet.scoreType === exercise.scoreSetConfigurations[0]?.scoreType,
      )
    ) {
      return getScoreTypeLabel(exercise.scoreSetConfigurations[0].scoreType)
    }
    return ""
  }

  function getScoreTypeCaption(): string {
    // All scoreSetConfigurations have the same scoreType -> we display the score result type caption
    if (
      exercise.scoreSetConfigurations.length > 1 &&
      !!exercise.scoringOption &&
      exercise.scoreSetConfigurations.every(
        (scoreSet) => scoreSet.scoreType === exercise.scoreSetConfigurations[0]?.scoreType,
      )
    ) {
      return getScoreResultTypeCaption(exercise.scoringOption, true)
    }

    return ""
  }

  function getScoreCompletionDisplayedScore(): string {
    // No score set completions -> we display the score type
    if (!exercise.score?.scoreSetCompletions || exercise.score.scoreSetCompletions.length === 0)
      return getScoreType() ?? ""
    // Time cap exceeded -> we display the time cap exceeded label
    if (
      exercise.score.scoreSetCompletions.some(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
      )
    ) {
      return word("session.score.form.timeCapExceeded.label")
    }
    // All scoreSetCompletions have the same scoreType -> we display the score
    else if (
      !!exercise.score.valueDecimal &&
      !!exercise.score.scoreSetCompletions[0] &&
      exercise.score.scoreSetCompletions.every(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === exercise.score?.scoreSetCompletions?.[0]?.scoreType,
      )
    ) {
      return getDisplayedScore(
        exercise.score.valueDecimal,
        exercise.score.additionalValueDecimal,
        exercise.score.scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
        exercise.score.timeCapExceeded ?? false,
        unit,
        language,
      )
    } else if (!!exercise.score.valueDecimal && exercise.score.scoreSetCompletions.length > 0) {
      const scoreSetConfigurationForScoring = exercise.scoreSetConfigurations.find(
        (scoreSetConfiguration) => scoreSetConfiguration.useForScoring,
      )
      if (!scoreSetConfigurationForScoring) throw new Error("Score set configuration not found")

      const scoreSetCompletion = exercise.score.scoreSetCompletions.find(
        (scoreSetCompletion) => scoreSetCompletion.scoreSetConfigurationId === scoreSetConfigurationForScoring.id,
      )
      if (!scoreSetCompletion) throw new Error("Score set completion not found")

      return getDisplayedScore(
        scoreSetCompletion.score ?? 0,
        scoreSetCompletion.scoreAdditional,
        scoreSetCompletion.scoreType,
        scoreSetCompletion.timeCapExceeded ?? false,
        unit,
        language,
      )
    } else {
      return ""
    }
  }

  function getDisplayedScoreCompletionDifficulty(): string | null {
    if (exercise.score?.scoreSetCompletions && exercise.score?.difficulty) {
      return getScoreDifficultyWording(exercise.score?.difficulty)
    }

    return null
  }

  function isScoreCompletionPersonalRecord(): boolean {
    return exercise.score?.personalRecord ?? false
  }

  function getScoreSetCompletionList(): string[] | null {
    if (
      exercise.score?.scoreSetCompletions &&
      exercise.score?.scoreSetCompletions.length > 0 &&
      exercise.score.scoreSetCompletions.every((scoreSetCompletion) => !scoreSetCompletion.timeCapExceeded)
    ) {
      return exercise.score.scoreSetCompletions.map((scoreSetCompletion) =>
        getDisplayedScore(
          scoreSetCompletion.score ?? 0,
          scoreSetCompletion.scoreAdditional,
          scoreSetCompletion.scoreType,
          scoreSetCompletion.timeCapExceeded ?? false,
          unit,
          language,
        ),
      )
    }
    return null
  }

  function displayCompleteExerciseScoreOrFeedbackButton(): boolean {
    return exercise.scoreSetConfigurations.length > 0 ? true : false
  }

  function shouldDisplayScoreSection(): boolean {
    return (
      exercise.scoreSetConfigurations.length > 0 &&
      !!exercise.scoreSetConfigurations[0] &&
      ![ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(exercise.scoreSetConfigurations[0].scoreType)
    )
  }

  return {
    scoreTypeCaption: getScoreTypeCaption(),
    displayedScore: getScoreCompletionDisplayedScore(),
    displayedScoreCompletionDifficulty: getDisplayedScoreCompletionDifficulty(),
    isScoreCompletionPersonalRecord: isScoreCompletionPersonalRecord(),
    displayedScoreSetCompletionList: getScoreSetCompletionList(),
    displayCompleteExerciseScoreOrFeedbackButton: displayCompleteExerciseScoreOrFeedbackButton(),
    shouldDisplayScoreSection: shouldDisplayScoreSection(),
  }
}
